import { SupportedPricingCurrencyType } from '@buggy/shared'
import { Skeleton } from 'antd'
import { isEmpty, isNil } from 'lodash-es'
import { CSSProperties, FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import { useFetchUserStatusQuery } from '@/api/user-api'
import Blur from '@/components/common/blur'
import { Duration } from '@/components/common/duration-select'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import KolDetailDataBlockSubHeader from '@/components/kol/common/kol-detail-data-block-sub-header'
import KolDetailStatistic from '@/components/kol/common/kol-detail-statistic'
import MentionedBrand, {
  type Brand,
} from '@/components/kol/common/mentioned-brand'
import { PostQuotationStatistic } from '@/components/kol/common/statistics/post-quotation-statistic'
import { VideoQuotationStatistic } from '@/components/kol/common/statistics/video-quotation-statistic'
import { initialPostFilterConditions } from '@/components/kol/detail/creative-content-analyses/post-list'
import { NoData } from '@/components/kol/detail/no-data'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import useMentionedBrands from '@/components/kol/hooks/use-mentioned-brands'
import { SafeNewTabLink } from '@/components/safe-new-tab-link'
import { mockMentionedBrands } from '@/constants/mock-data/mentioned-brands'
import { Page } from '@/hooks/use-authorization/constants'
import useFormatPrice from '@/hooks/use-format-price'
import { useI18n } from '@/hooks/use-i18n'
import { usePermissions } from '@/hooks/use-permissions'
import { useShouldDisplayQuotations } from '@/hooks/use-should-display-quotations'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Optional } from '@/types/optional'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli } from '@/utils/ampli'
import { Platform, PlatformShortcode } from '@/utils/convert-platform'
import {
  getPostValuation,
  getVideoValuation,
} from '@/utils/detail/get-valuation'
import getDateRangeFromETLAt from '@/utils/get-date-range-from-etl-at'
import { getThumbnail } from '@/utils/get-thumbnails'
import mapCurrency, {
  getCurrencyCodeFromCountryCode,
  getCurrencyCodeFromLanguage,
} from '@/utils/map-currency'

interface CollaborationValueEstimationProps {
  duration: Duration.ThreeMonths | Duration.SixMonths
  platform: Platform
  kolInfo?: KolSchemaModel
  isKolInfoLocked: boolean
  loading?: boolean
  showMockData?: boolean
}

const CollaborationValueEstimation: FunctionComponent<
  CollaborationValueEstimationProps
> = ({
  duration,
  platform,
  kolInfo,
  isKolInfoLocked,
  loading,
  showMockData,
}) => {
  const { formatMessage } = useIntl()
  const { formatPrice } = useFormatPrice()
  const { page, isPublicKolDetailPage, searchParams, setSearchParams } =
    useKolDetailRoute()
  const { rootLang } = useI18n()
  const { data: userStatus } = useFetchUserStatusQuery()
  const shouldDisplayQuotations = useShouldDisplayQuotations()
  const { withKolPermission } = usePermissions()
  const {
    mentionedBrands: mentionedBrandsData,
    isFetching: isFetchingMentionedBrands,
  } = useMentionedBrands({
    kolId: kolInfo?.id,
    isKolInfoLocked,
    duration,
    platform: platform.shortcode,
    perPage: 10,
  })

  const kpi = kolInfo?.kpi.find((kpi) => kpi.platform === platform.shortcode)

  const currencyCode = ((): SupportedPricingCurrencyType => {
    if (isPublicKolDetailPage) {
      return getCurrencyCodeFromLanguage(rootLang)
    }

    if ([Page.KolSelf, Page.KolAgencyId].includes(page)) {
      return getCurrencyCodeFromCountryCode(kolInfo?.countryCode)
    }

    return mapCurrency(userStatus?.currentWorkspace?.currencyCode)
  })()
  const quotations = kpi?.quotations
  const displayQuotations =
    [Page.KolId, Page.PublicKolDetail].includes(page) &&
    shouldDisplayQuotations(platform.shortcode)
  const mentionedBrands: Brand[] = isKolInfoLocked
    ? mockMentionedBrands
    : (mentionedBrandsData?.data.map((brand) => ({
        name: brand.name,
        platformName: brand.platformName,
        thumbnailUrl: getThumbnail(brand.uuid),
        mentionCount: brand.mentionCount,
        etlAt: brand.etlAt,
      })) ?? [])

  const handleClickValuationConfigLink = (): void => {
    ampli.clickWommSetting({
      kolId: kolInfo?.id,
      kolName: kolInfo?.name,
      platform: platform.shortcode,
      section: 'Collaboration and Value',
    })
  }

  return (
    <KolDetailDataBlock
      colSpan={12}
      header={{
        title: formatMessage(
          {
            id: 'kol:collaboration_value_estimation_data_block_title',
          },
          { platformTitle: platform.title },
        ),
        currencyCode,
      }}
      kolId={kolInfo?.id}
      kolMetrics='Collaboration and Value'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
    >
      <Flex direction='column' size={30}>
        <Flex direction='column' size={20}>
          {displayQuotations && (
            <Flex size={20}>
              <PostQuotationStatistic
                currencyCode={currencyCode}
                isKolInfoLocked={isKolInfoLocked}
                loading={loading}
                quotationPriceRange={quotations?.post}
                showMockData={showMockData}
              />
              <VideoQuotationStatistic
                currencyCode={currencyCode}
                isKolInfoLocked={isKolInfoLocked}
                loading={loading}
                quotationPriceRange={quotations?.video}
                showMockData={showMockData}
              />
            </Flex>
          )}
          <Flex size={20} wrap='wrap'>
            {platform.shortcode !== PlatformShortcode.YouTube && (
              <Statistic
                loading={loading}
                title={formatMessage({
                  id: 'kol:collaboration_value_estimation_data_block_post_valuation_statistic_title',
                })}
                tooltip={{
                  title: formatMessage({
                    id: 'kol:platform_evaluation_more_post_tooltip',
                  }),
                }}
                value={((): ReactNode => {
                  if (isKolInfoLocked) {
                    return (
                      <Blur activate={!showMockData}>
                        {formatPrice(139599)}
                      </Blur>
                    )
                  }

                  const price = getPostValuation(duration, kpi)

                  if (isNil(price) || price === 0) {
                    return null
                  }

                  return formatPrice(price)
                })()}
              />
            )}
            <Statistic
              loading={loading}
              title={formatMessage({
                id: 'kol:collaboration_value_estimation_data_block_video_valuation_statistic_title',
              })}
              tooltip={{
                title: formatMessage({
                  id: 'kol:platform_evaluation_more_video_tooltip',
                }),
              }}
              value={((): ReactNode => {
                if (isKolInfoLocked) {
                  return (
                    <Blur activate={!showMockData}>{formatPrice(139599)}</Blur>
                  )
                }

                const price = getVideoValuation(duration, kpi)

                if (isNil(price) || price === 0) {
                  return null
                }

                return formatPrice(price)
              })()}
            />
          </Flex>
          {!withKolPermission && !isPublicKolDetailPage && !loading && (
            <ValuationConfigText>
              {formatMessage(
                {
                  id: 'kol:collaboration_value_estimation_data_block_valuation_config',
                },
                {
                  link: (
                    <ValuationConfigLink
                      href={Page.WorkspaceValuation}
                      onClick={handleClickValuationConfigLink}
                    >
                      {formatMessage({
                        id: 'kol:collaboration_value_estimation_data_block_valuation_config_link',
                      })}
                    </ValuationConfigLink>
                  ),
                },
              )}
            </ValuationConfigText>
          )}
        </Flex>
        {platform.shortcode === PlatformShortcode.Instagram &&
          (isFetchingMentionedBrands || loading ? (
            <MentionedBrandSkeleton
              active
              paragraph={{ rows: 3, width: '20%' }}
              title={false}
            />
          ) : (
            <Flex direction='column' size={20}>
              <KolDetailDataBlockSubHeader
                title={formatMessage({
                  id: 'kol:collaboration_value_estimation_data_block_mention_brand_sub_header_title',
                })}
                tooltip={{
                  title: formatMessage(
                    {
                      id: 'kol:collaboration_value_estimation_data_block_mention_brand_sub_header_tooltip',
                    },
                    { platformTitle: platform.title },
                  ),
                }}
              />
              {isEmpty(mentionedBrands) ? (
                <NoData />
              ) : (
                <MentionedBrandGrid activate={isKolInfoLocked && !showMockData}>
                  {mentionedBrands.map((brand) => {
                    return (
                      <MentionedBrand
                        brand={brand}
                        isKolInfoLocked={isKolInfoLocked}
                        key={brand.name}
                        onClick={async (): Promise<void> => {
                          if (isKolInfoLocked) {
                            return
                          }

                          const [postStartDate, postEndDate] =
                            getDateRangeFromETLAt(brand.etlAt, duration)

                          await setSearchParams(
                            {
                              ...searchParams.data,
                              ...initialPostFilterConditions,
                              keyword: brand.platformName,
                              postListPlatform: platform.shortcode,
                              postListDateRangeStart:
                                postStartDate.toISOString(),
                              postListDateRangeEnd: postEndDate.toISOString(),
                            },
                            'postList',
                          )
                        }}
                      />
                    )
                  })}
                </MentionedBrandGrid>
              )}
            </Flex>
          ))}
      </Flex>
    </KolDetailDataBlock>
  )
}

const Flex = styled.div<{
  size: number
  direction?: CSSProperties['flexDirection']
  wrap?: CSSProperties['flexWrap']
}>`
  display: flex;
  flex-direction: ${({ direction }): Optional<string> => direction};
  flex-wrap: ${({ wrap }): Optional<string> => wrap};
  gap: ${({ size }): number => size}px;
`

const Statistic = styled(KolDetailStatistic)`
  flex: 1;
`

const ValuationConfigText = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

const ValuationConfigLink = styled(SafeNewTabLink)`
  color: ${({ theme }): string => theme.colors.brand.primary};
  text-decoration: underline;

  &:hover,
  &:active,
  &:focus {
    color: ${({ theme }): string => theme.colors.brand.primaryHover};
    text-decoration: underline;
  }
`

const MentionedBrandSkeleton = styled(Skeleton)`
  margin: 0 0 30px;
`

const MentionedBrandGrid = styled(Blur)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: auto;
  grid-gap: 10px;

  @media (min-width: 981px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

export default CollaborationValueEstimation
